/**
 * PaymentPaywallModal - Main modal component for payment recovery
 *
 * Displays a full-screen overlay when users need to update their payment method
 * or create a new subscription.
 *
 * Supports two flows based on flowType:
 * - 'update_payment_method': For dunning/past_due - uses SetupIntent (card only)
 * - 'create_subscription': For canceled users - uses PaymentIntent with Express Checkout
 *
 * Usage:
 * ```tsx
 * const { shouldShowPaywall, context: reason, subscriptionId, flowType, price } = usePaywallDetection();
 *
 * {shouldShowPaywall && (
 *   <PaymentPaywallModal
 *     reason={reason}
 *     subscriptionId={subscriptionId}
 *     flowType={flowType}
 *     price={price}
 *     onSuccess={() => navigate(ROUTE_DASHBOARD)}
 *   />
 * )}
 * ```
 */

import { Elements } from '@stripe/react-stripe-js';
import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import type { PaywallPriceResponse } from '@/api/generated-catch-backend/model';
import {
  buildPriceData,
  PriceDataType,
} from '@/app/components/payment/components/method/payment-method-v2.controller';
import usePaymentController from '@/app/components/payment/payment.controller';
import MembershipSummary from '@/app/components/ppra/components/ppra-payment-page/components/MembershipSummary';
import FeatureList from '@/app/components/ppra/components/ppra-payment-page/components/FeatureList';
import { PaywallHeader, PaywallReason } from './components/PaywallHeader';
import { PaywallPaymentForm } from './components/PaywallPaymentForm';
import { PaywallSuccessView } from './components/PaywallSuccessView';
import { PaywallErrorView } from './components/PaywallErrorView';
import { useUpdatePaymentMethod } from './useUpdatePaymentMethod';
import { useCreateSubscription } from './useCreateSubscription';
import { useDownloadLabResults } from '@/app/components/labs-order/hooks/useDownloadLabResults';
import useAnalyticsCalls from '@/hooks/use-analytics';
import type { PaywallFlowType } from './usePaywallDetection';
import './paywall-reset.css';

interface PaymentPaywallModalProps {
  reason: PaywallReason | null;
  subscriptionId: string | null;
  flowType: PaywallFlowType | null;
  price: PaywallPriceResponse | null;
  labOrderId: string | null;
  onSuccess: () => void;
}

function PaymentPaywallModalContent({
  reason,
  subscriptionId,
  flowType,
  price,
  labOrderId,
  onSuccess,
}: PaymentPaywallModalProps) {
  const [step, setStep] = useState<'info' | 'payment'>('info');
  const [showError, setShowError] = useState(false);
  const { downloadResults, isDownloading } = useDownloadLabResults();
  const { callProductPaywall, callProductPaymentPage } = useAnalyticsCalls();

  // Compute paywall type for analytics
  const paywallType = useMemo(() => {
    if (flowType === 'update_payment_method') return 'Fix Payment' as const;
    // create_subscription flow
    if (reason?.isPaymentFailed) return 'Fix Payment + Cancel' as const;
    return 'Subscribe' as const;
  }, [flowType, reason]);

  // Track Product Paywall on mount (fire once only)
  const hasTrackedPaywall = useRef(false);
  useEffect(() => {
    if (!hasTrackedPaywall.current) {
      callProductPaywall(paywallType);
      hasTrackedPaywall.current = true;
    }
  }, [callProductPaywall, paywallType]);

  // Build price data from the paywall context (already fetched by usePaywallDetection)
  const priceData: PriceDataType | null = useMemo(() => {
    if (!price) return null;
    return buildPriceData(price.subtotal, price.discount, price.tax, price.total);
  }, [price]);

  const handleError = useCallback(() => {
    setShowError(true);
  }, []);

  // Determine which flow to use
  const isCreateSubscriptionFlow = flowType === 'create_subscription';

  // For update_payment_method flow, use SetupIntent to update payment method
  const updatePaymentHook = useUpdatePaymentMethod({
    subscriptionId: subscriptionId ?? '',
    onError: handleError,
  });

  // For create_subscription flow, create a new subscription
  const createSubscriptionHook = useCreateSubscription({
    onError: handleError,
  });

  // Use the appropriate hook based on flow type
  const {
    state,
    error,
    reset,
  } = isCreateSubscriptionFlow ? createSubscriptionHook : updatePaymentHook;

  // Get the appropriate submit handler based on flow type
  const handleSubmit = isCreateSubscriptionFlow
    ? createSubscriptionHook.createSubscription
    : updatePaymentHook.updatePaymentMethod;

  // Get express checkout handler - available for both flows
  const handleExpressCheckout = isCreateSubscriptionFlow
    ? createSubscriptionHook.handleExpressCheckout
    : updatePaymentHook.handleExpressCheckout;

  const handleRetry = useCallback(() => {
    setShowError(false);
    reset();
  }, [reset]);

  const handleProceedToPayment = useCallback(() => {
    callProductPaymentPage('paywall');
    setStep('payment');
  }, [callProductPaymentPage]);

  const isSuccess = state === 'success';

  // Default price data while loading
  const displayPriceData = priceData ?? buildPriceData(0, 0, 0, 0);

  // CTA button text - always "Update Payment" on info screen
  const ctaButtonText = 'Update Payment';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/60 p-4">
      <div className="relative w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl">
        {/* Scrollable content area */}
        <div className="max-h-[90vh] overflow-y-auto px-7 py-10">
          {isSuccess ? (
            <PaywallSuccessView onNavigateToDashboard={onSuccess} />
          ) : step === 'info' ? (
            /* Step 1: Info view with pricing and features */
            <div className="paywall-reset flex flex-col gap-5">
              {/* Header message */}
              {reason && <PaywallHeader reason={reason} />}

              {/* Membership pricing card */}
              <MembershipSummary
                priceData={displayPriceData}
                isCouponVisible={false}
                showCoupon={() => undefined}
                coupon=""
                setCoupon={() => undefined}
                couponValidationError={null}
                isSubmitting={false}
                getPrice={() => Promise.resolve()}
                isSinglePaymentEnabled={false}
                isTrial={false}
                isPriceLoading={!price}
              />

              {/* Feature list */}
              <FeatureList />

              {/* CTA Button */}
              <button
                onClick={handleProceedToPayment}
                className="flex w-full items-center justify-center rounded-full bg-[#7CF1C7] px-5 py-5 text-sm font-bold leading-[1.4] text-[#183344] transition-opacity hover:opacity-90"
              >
                {ctaButtonText}
              </button>

              {/* Download Raw Results link - only shown when user has processed labs */}
              {labOrderId && (
                <button
                  onClick={() => downloadResults(labOrderId)}
                  disabled={isDownloading}
                  className="mt-2 w-full text-center text-sm font-medium text-[#183344] underline transition-opacity hover:opacity-70 disabled:opacity-50"
                >
                  {isDownloading ? 'Downloading...' : 'Download Raw Results'}
                </button>
              )}
            </div>
          ) : (
            /* Step 2: Payment form */
            <div className="flex flex-col gap-5">
              {/* Error message */}
              {showError && error && (
                <PaywallErrorView errorMessage={error} onRetry={handleRetry} />
              )}

              {/* Membership pricing card */}
              <MembershipSummary
                priceData={displayPriceData}
                isCouponVisible={false}
                showCoupon={() => undefined}
                coupon=""
                setCoupon={() => undefined}
                couponValidationError={null}
                isSubmitting={false}
                getPrice={() => Promise.resolve()}
                isSinglePaymentEnabled={false}
                isTrial={false}
                isPriceLoading={!price}
              />

              {/* Payment form - different flows based on flowType */}
              <PaywallPaymentForm
                state={state}
                onSubmit={handleSubmit}
                flowType={flowType}
                priceAmount={price?.total}
                onExpressCheckout={handleExpressCheckout}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function PaymentPaywallModal(props: PaymentPaywallModalProps) {
  const [amount, setAmount] = useState<number | undefined>();

  // Listen for amount updates from price fetching
  useEffect(() => {
    const handleAmountUpdate = (event: CustomEvent<{ amount: number }>) => {
      setAmount(event.detail.amount);
    };

    window.addEventListener('stripe-amount-update', handleAmountUpdate as EventListener);
    return () => {
      window.removeEventListener('stripe-amount-update', handleAmountUpdate as EventListener);
    };
  }, []);

  // Use shared Stripe configuration from payment controller
  // - update_payment_method: uses 'setup' mode (save card without charging)
  // - create_subscription: uses 'subscription' mode (charge for subscription)
  const stripeMode = props.flowType === 'create_subscription' ? 'subscription' : 'setup';
  const { stripePromise, stripeOptions } = usePaymentController({
    mode: stripeMode,
    amount,
  });

  return (
    <Elements stripe={stripePromise} options={stripeOptions} key={`${props.flowType}-${amount}`}>
      <PaymentPaywallModalContent {...props} />
    </Elements>
  );
}
